import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './useAuth'
import { useTransactions } from './useTransactions'
import { budgetService } from '../services/budgetService'
import { AIBudget } from '../types/ai-budget'
import { Transaction } from '../types/dashboard'

interface BudgetTracking {
  id: string
  budget_id: string
  category: string
  allocated_amount: number
  spent_amount: number
  month_year: string
  created_at: string
}

interface BudgetTransactionData {
  budget: AIBudget | null
  tracking: BudgetTracking[]
  transactions: Transaction[]
  loading: boolean
  error: string | null
  refreshData: () => Promise<void>
  getCategoryProgress: (categoryName: string) => {
    allocated: number
    spent: number
    remaining: number
    percentage: number
    status: 'good' | 'warning' | 'danger'
  }
  getTotalProgress: () => {
    totalAllocated: number
    totalSpent: number
    totalRemaining: number
    overallPercentage: number
  }
  getTransactionsForCategory: (categoryName: string) => Transaction[]
  syncTransactionsWithBudget: () => Promise<void>
}

export const useBudgetTransactions = (monthYear?: string): BudgetTransactionData => {
  const { user } = useAuth()
  const { transactions } = useTransactions()
  const [budget, setBudget] = useState<AIBudget | null>(null)
  const [tracking, setTracking] = useState<BudgetTracking[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch budget and tracking data
  const fetchBudgetData = useCallback(async () => {
    if (!user) {
      setBudget(null)
      setTracking([])
      setLoading(false)
      return
    }

    console.log('📊 useBudgetTransactions: Fetching budget and tracking data...')
    
    try {
      setLoading(true)
      setError(null)

      // Get active budget
      const activeBudget = await budgetService.getActiveBudget(user.id)
      setBudget(activeBudget)

      if (activeBudget) {
        // Get tracking data for the specified month or current month
        const targetMonth = monthYear || new Date().toISOString().slice(0, 7)
        const trackingData = await budgetService.getBudgetTracking(activeBudget.id, targetMonth)
        setTracking(trackingData)
        
        console.log(`📊 useBudgetTransactions: Found ${trackingData.length} tracking entries for ${targetMonth}`)
      } else {
        setTracking([])
        console.log('📊 useBudgetTransactions: No active budget found')
      }
    } catch (err) {
      console.error('❌ useBudgetTransactions: Error fetching budget data:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch budget data'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [user, monthYear])

  // Refresh all data
  const refreshData = useCallback(async () => {
    await fetchBudgetData()
  }, [fetchBudgetData])

  // Get progress for a specific category
  const getCategoryProgress = useCallback((categoryName: string) => {
    const categoryTracking = tracking.find(t => t.category === categoryName)
    
    if (!categoryTracking) {
      return {
        allocated: 0,
        spent: 0,
        remaining: 0,
        percentage: 0,
        status: 'good' as const
      }
    }

    const allocated = categoryTracking.allocated_amount
    const spent = categoryTracking.spent_amount
    const remaining = allocated - spent
    const percentage = allocated > 0 ? (spent / allocated) * 100 : 0

    let status: 'good' | 'warning' | 'danger'
    if (percentage >= 95) {
      status = 'danger'
    } else if (percentage >= 75) {
      status = 'warning'
    } else {
      status = 'good'
    }

    return {
      allocated,
      spent,
      remaining,
      percentage: Math.round(percentage),
      status
    }
  }, [tracking])

  // Get total progress across all categories
  const getTotalProgress = useCallback(() => {
    const totalAllocated = tracking.reduce((sum, t) => sum + t.allocated_amount, 0)
    const totalSpent = tracking.reduce((sum, t) => sum + t.spent_amount, 0)
    const totalRemaining = totalAllocated - totalSpent
    const overallPercentage = totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0

    return {
      totalAllocated,
      totalSpent,
      totalRemaining,
      overallPercentage: Math.round(overallPercentage)
    }
  }, [tracking])

  // Get transactions that map to a specific budget category
  const getTransactionsForCategory = useCallback((categoryName: string) => {
    if (!budget || !transactions.length) {
      return []
    }

    const targetMonth = monthYear || new Date().toISOString().slice(0, 7)
    
    return transactions.filter(transaction => {
      // Only include expense transactions from the target month
      if (transaction.type !== 'expense' || !transaction.category) {
        return false
      }

      const transactionMonth = new Date(transaction.date).toISOString().slice(0, 7)
      if (transactionMonth !== targetMonth) {
        return false
      }

      // Find the budget category this transaction maps to
      const budgetCategory = budget.budget_categories.find(cat => cat.name === categoryName)
      if (!budgetCategory) {
        return false
      }

      // For now, we'll use a simple mapping - in a real implementation,
      // you'd use the categoryMappingService here
      // This is a simplified version for demonstration
      const transactionCategory = transaction.category.toLowerCase()
      const budgetCategoryName = categoryName.toLowerCase()

      // Simple keyword matching
      if (budgetCategoryName.includes('food') && transactionCategory === 'food') return true
      if (budgetCategoryName.includes('transport') && transactionCategory === 'transportation') return true
      if (budgetCategoryName.includes('entertainment') && transactionCategory === 'entertainment') return true
      if (budgetCategoryName.includes('bills') && transactionCategory === 'bills') return true
      if (budgetCategoryName.includes('healthcare') && transactionCategory === 'healthcare') return true
      if (budgetCategoryName.includes('education') && transactionCategory === 'education') return true
      if (budgetCategoryName.includes('travel') && transactionCategory === 'travel') return true
      if (budgetCategoryName.includes('personal') && transactionCategory === 'personal') return true
      if (budgetCategoryName.includes('shopping') && transactionCategory === 'shopping') return true

      return false
    })
  }, [budget, transactions, monthYear])

  // Sync existing transactions with the budget
  const syncTransactionsWithBudget = useCallback(async () => {
    if (!budget || !transactions.length) {
      console.log('📊 useBudgetTransactions: No budget or transactions to sync')
      return
    }

    console.log('🔄 useBudgetTransactions: Syncing transactions with budget...')

    try {
      setError(null)
      await budgetService.syncTransactionsWithBudget(budget.id, transactions)
      console.log('✅ useBudgetTransactions: Transaction sync completed')
      
      // Refresh the data to show updated tracking
      await fetchBudgetData()
    } catch (err) {
      console.error('❌ useBudgetTransactions: Error syncing transactions:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to sync transactions'
      setError(errorMessage)
    }
  }, [budget, transactions, fetchBudgetData])

  // Fetch data when dependencies change
  useEffect(() => {
    fetchBudgetData()
  }, [fetchBudgetData])

  // Check for month rollover on mount
  useEffect(() => {
    if (user) {
      budgetService.handleMonthRollover(user.id).catch(error => {
        console.warn('⚠️ Month rollover check failed:', error)
      })
    }
  }, [user])

  return {
    budget,
    tracking,
    transactions,
    loading,
    error,
    refreshData,
    getCategoryProgress,
    getTotalProgress,
    getTransactionsForCategory,
    syncTransactionsWithBudget
  }
}
